import { FILE_ACCEPT } from '@fos/shared/contracts/knowledge';
import { Camera, Upload } from 'lucide-react';
import { type DragEvent, type ReactNode, useId, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { checkFileBeforeUpload } from '../lib/files-api';
import { Spinner } from './Spinner';

/** Plik odrzucony przed wysłaniem (pusty, za duży, niedozwolony typ) z komunikatem po polsku. */
export interface RejectedFile {
  file: File;
  message: string;
}

/** Podział wybranych plików na dozwolone i odrzucone (szybka kontrola przed wysłaniem; serwer i tak sprawdza treść). */
export function splitFiles(list: Iterable<File>, max: number): { accepted: File[]; rejected: RejectedFile[] } {
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  for (const file of list) {
    const problem = checkFileBeforeUpload(file);
    if (problem) rejected.push({ file, message: problem });
    else if (accepted.length < max) accepted.push(file);
    else rejected.push({ file, message: `Naraz najwyżej ${max} plików.` });
  }
  return { accepted, rejected };
}

const DEFAULT_HINT = 'PDF, Word, Excel, obrazy, TXT, CSV i ZIP — do 25 MB';

/**
 * Wgrywanie plików (DECISIONS 2026-10-17 „Dokumenty klienta”): pole „przeciągnij i upuść” + wybór z dysku, na telefonie
 * dodatkowo „Zrób zdjęcie” (aparat). Sam komponent nie wysyła — sprawdza typ i rozmiar (`checkFileBeforeUpload`)
 * i oddaje pliki (`onFiles`) albo odrzucone z komunikatem (`onReject`); wysyła widok (`lib/files-api.uploadFile`).
 *
 * Dostępność: całe pole to `<label>` ukrytego `<input type="file">` (klik w dowolne miejsce otwiera wybór, klawiatura
 * — Tab do pola i Enter / Spacja), obrys fokusu na polu. `size="l"` — duże pole (pusty stan listy), `size="s"` —
 * niski pasek nad listą.
 */
export function FileDropzone({
  onFiles,
  onReject,
  multiple = true,
  max = 10,
  disabled = false,
  busy = false,
  size = 'l',
  title,
  hint = DEFAULT_HINT,
  camera = true,
  className,
  'aria-label': ariaLabel,
}: {
  onFiles: (files: File[]) => void;
  onReject?: (rejected: RejectedFile[]) => void;
  multiple?: boolean;
  /** najwięcej plików naraz (reszta trafia do odrzuconych) */
  max?: number;
  disabled?: boolean;
  /** trwa wysyłanie: spinner zamiast ikony, pole nieaktywne */
  busy?: boolean;
  size?: 's' | 'l';
  title?: ReactNode;
  hint?: ReactNode;
  /** „Zrób zdjęcie” na telefonie (aparat, tylko obrazy) */
  camera?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  const id = useId();
  const cameraId = useId();
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const off = disabled || busy;
  const limit = multiple ? max : 1;

  const take = (list: FileList | null | undefined) => {
    if (!list || list.length === 0) return;
    const { accepted, rejected } = splitFiles(Array.from(list), limit);
    if (rejected.length > 0) onReject?.(rejected);
    if (accepted.length > 0) onFiles(accepted);
  };

  const onDragEnter = (e: DragEvent) => {
    if (off || !Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
    e.preventDefault();
    depth.current += 1;
    setOver(true);
  };
  const onDragOver = (e: DragEvent) => {
    if (off) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = () => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    if (off) return;
    take(e.dataTransfer?.files);
  };

  const heading =
    title ??
    (multiple ? 'Przeciągnij pliki tutaj albo wybierz z dysku' : 'Przeciągnij plik tutaj albo wybierz z dysku');

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label
        htmlFor={id}
        data-dragging={over || undefined}
        aria-busy={busy || undefined}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'group relative flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-line-strong bg-surface text-left transition-colors',
          'hover:border-fg-muted hover:bg-elevated',
          'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-accent',
          size === 'l'
            ? 'flex-col justify-center px-4 py-8 text-center md:py-10'
            : 'min-h-14 px-4 py-3 max-md:min-h-16',
          over && 'border-accent bg-accent-subtle',
          off && 'cursor-not-allowed opacity-60 hover:border-line-strong hover:bg-surface',
        )}
      >
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full bg-panel text-fg-secondary',
            size === 'l' ? 'size-10' : 'size-8',
            over && 'text-accent',
          )}
          aria-hidden
        >
          {busy ? <Spinner size={16} /> : <Upload size={size === 'l' ? 20 : 16} strokeWidth={1.75} />}
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-body-s font-medium text-fg">{over ? 'Upuść, aby dodać' : heading}</span>
          {hint && <span className="text-caption text-fg-muted">{hint}</span>}
        </span>
        <input
          id={id}
          type="file"
          accept={FILE_ACCEPT}
          multiple={multiple}
          disabled={off}
          aria-label={ariaLabel}
          className="sr-only"
          onChange={(e) => {
            take(e.target.files);
            e.target.value = '';
          }}
        />
      </label>
      {camera && (
        <label
          htmlFor={cameraId}
          className={cn(
            'inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md bg-soft px-3 text-body-s font-medium text-fg-secondary md:hidden',
            'hover:bg-soft-hover hover:text-fg has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-accent',
            off && 'cursor-not-allowed opacity-60',
          )}
        >
          <Camera size={16} strokeWidth={1.75} aria-hidden />
          Zrób zdjęcie
          <input
            id={cameraId}
            type="file"
            accept="image/*"
            capture="environment"
            disabled={off}
            className="sr-only"
            onChange={(e) => {
              take(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      )}
    </div>
  );
}
